import { DemoDataSchema, type AppClient, type DemoData, type EventEnvelope } from '@pointfive/contracts';
import { DEMO_AS_OF } from './dataset';
import { bindAdmin } from './families/admin';
import { bindAnalyse } from './families/analyse';
import { bindAuth } from './families/auth';
import { bindBilling } from './families/billing';
import { bindDashboard } from './families/dashboard';
import { bindEngine } from './families/engine';
import { bindExecute } from './families/execute';
import { bindManage } from './families/manage';
import { bindMe } from './families/me';
import { bindNotifications } from './families/notifications';
import { bindPortal } from './families/portal';
import { bindPrepare } from './families/prepare';
import { bindPublic } from './families/public';
import { bindReview } from './families/review';
import { bindVoice } from './families/voice';
import { createSimRuntime, type InjectOptions, type InjectState } from './runtime';
import { createSimState } from './state';

/** Phase-1 in-process contracts client (sim link). Every family of `AppClient` is implemented over the seeded dataset;
 *  Phase 2 replaces the link with tRPC over HTTP — same call shapes. Nothing here is live. */
export interface SimClientOptions {
  seed?: string;
}
export interface SimControls {
  /** Failure injection for /dev/console: consume-once flags (`rejectNext`, `unconfirmedNext`, `rateLimitNext`) and
   *  sticky modes (`evaluatorDown`, `brokerDegraded`). */
  inject(opts: InjectOptions): InjectState;
  /** The reconciler: finalises every `unconfirmed` intent as done (the simulated broker reports the fill). */
  reconcile(): number;
  /** Outbox rows recorded so far (ADR-003). */
  outbox(): readonly EventEnvelope[];
  /** Procedure call log (name per call) — proves the aggregate is a composition of family reads. */
  calls(): readonly string[];
}
export type SimClient = AppClient & {
  readonly mode: 'simulated';
  /** Whole read model for the Phase-1 UI — assembled by calling the per-family procedures (see `composeDataset`). */
  readonly demo: { dataset: () => Promise<DemoData> };
  readonly sim: SimControls;
};

export function createSimClient(opts: SimClientOptions = {}): SimClient {
  const rt = createSimRuntime(opts.seed);
  const st = createSimState(rt);
  const calls: string[] = [];
  const onCall = (name: string) => {
    calls.push(name);
  };
  const auth = bindAuth(rt);
  const me = bindMe(rt, st);
  const dashboard = bindDashboard(rt);
  const prepare = bindPrepare(rt, st);
  const execute = bindExecute(rt, st);
  const review = bindReview(rt, st);
  const analyse = bindAnalyse(rt, st);
  const manage = bindManage(rt, st);
  const billing = bindBilling(rt);
  const notifications = bindNotifications(rt, st);
  const voice = bindVoice(rt, st);
  const engine = bindEngine(rt, st);
  const admin = bindAdmin(rt, st);
  const pub = bindPublic(rt, st);
  const portal = bindPortal(rt, st);

  const client: AppClient = {
    mode: 'simulated',
    auth: auth.auth,
    me: me.me,
    dashboard: dashboard.dashboard,
    prepare: prepare.prepare,
    execute: execute.execute,
    review: review.review,
    analyse: analyse.analyse,
    manage: { ...manage.manage, ...billing.manage, ...notifications.manage },
    voice: voice.voice,
    engine: engine.engine,
    admin: admin.admin,
    public: pub.public,
    portal: portal.portal,
  };
  const traced = traceCalls(client, onCall);

  const sim: SimControls = {
    inject: (o) => {
      Object.assign(rt.inject, o);
      return { ...rt.inject };
    },
    reconcile: () => {
      let n = 0;
      for (const rec of st.intents.values()) {
        if (rec.intent.status !== 'unconfirmed') continue;
        rt.clock.tick();
        rec.intent.status = 'done';
        rec.intent.finalisedAt = rt.clock.now();
        rec.intent.reason = 'Reconciled: broker reported no fill for the unconfirmed intent.';
        rec.result = {
          meta: rt.meta(),
          intent: { ...rec.intent },
          order: null,
          positions: st.positions.map((p) => ({ ...p })),
        };
        n += 1;
      }
      return n;
    },
    outbox: () => rt.outbox,
    calls: () => calls,
  };

  return {
    ...traced,
    mode: 'simulated',
    demo: { dataset: () => composeDataset(traced, opts.seed ?? rt.seed) },
    sim,
  };
}
export type { InjectOptions, InjectState } from './runtime';
export const SIM_MODE = 'simulated' as const;

/** Wraps every procedure so the call name is logged — transport-level tracing the tRPC link will also provide. */
function traceCalls(client: AppClient, onCall: (name: string) => void): AppClient {
  const out: Record<string, unknown> = { mode: client.mode };
  for (const [domain, modules] of Object.entries(client)) {
    if (domain === 'mode' || typeof modules !== 'object' || modules === null) continue;
    const dom: Record<string, Record<string, (input: unknown) => Promise<unknown>>> = {};
    for (const [mod, verbs] of Object.entries(
      modules as Record<string, Record<string, (input: unknown) => Promise<unknown>>>,
    )) {
      const m: Record<string, (input: unknown) => Promise<unknown>> = {};
      for (const [verb, fn] of Object.entries(verbs)) {
        m[verb] = (input: unknown) => {
          onCall(`${domain}.${mod}.${verb}`);
          return fn(input);
        };
      }
      dom[mod] = m;
    }
    out[domain] = dom;
  }
  return out as unknown as AppClient;
}

/** Drains a cursor-paginated read (max page 200): every item plus the first page (for its non-item fields). */
async function drainPages<P extends { items: unknown[]; nextCursor: string | null }>(
  fetch: (cursor: string | null) => Promise<P>,
): Promise<{ first: P; items: P['items'] }> {
  const first = await fetch(null);
  const items: P['items'] = [...first.items];
  let cursor = first.nextCursor;
  while (cursor) {
    const page: P = await fetch(cursor);
    items.push(...page.items);
    cursor = page.nextCursor;
  }
  return { first, items };
}
const drain = async <P extends { items: unknown[]; nextCursor: string | null }>(
  fetch: (cursor: string | null) => Promise<P>,
): Promise<P['items']> => (await drainPages(fetch)).items;

/* ── absence, as data ──────────────────────────────────────────────────────────────────────────────────────────
 *
 *  The composed dataset was all-or-nothing. Twenty-nine reads in one `Promise.all`, so the first rejection unwound
 *  the whole tree and the product rendered "Your data could not be loaded" on every screen. That is right when the
 *  database is unreachable. It is badly wrong when a card is simply empty — and a brand-new account is nothing BUT
 *  empty cards: no replay prepared, no report issued, no brief generated, nothing traded this week. Seven refusals
 *  were found that way, each only visible after fixing the one before it, because the page died at the first.
 *
 *  What separates the two is the refusal's own code, which the kernel and the handlers were already careful about:
 *
 *    · CONFLICT, UPSTREAM_DEGRADED, NOT_ENTITLED — "there is nothing here for you". A card, empty. Ordinary.
 *    · anything else — INTERNAL, VALIDATION, a dead socket, a schema mismatch. Something is broken and the page
 *      should say so rather than paper over it with a plausible blank.
 *
 *  So absence is caught and NAMED, never swallowed: every tolerated refusal is recorded in `meta.degraded` so a card
 *  can tell a trader it has nothing rather than looking like a card whose data merely failed to arrive. A blanket
 *  `catch` would have been four characters shorter and would have hidden the next real defect for as long as this
 *  one hid the six behind it.
 */
// The codes that mean "there is nothing here". `NOT_FOUND` was in this set and is not a code the product has —
// the catalogue in packages/contracts/src/errors.ts is closed and does not include it, so the entry matched nothing
// and read as coverage it never provided.
const ABSENCE_CODES = new Set(['CONFLICT', 'UPSTREAM_DEGRADED', 'NOT_ENTITLED']);

function absenceCode(error: unknown): string | null {
  const code = (error as { code?: unknown; shape?: { code?: unknown } } | null)?.code;
  const named = typeof code === 'string' ? code : null;
  return named && ABSENCE_CODES.has(named) ? named : null;
}

/** Runs an optional read; an absence becomes `null` and is recorded.
 *
 *  Null rather than a substituted response, because the caller knows what empty means for the two or three fields it
 *  actually reads and this function would have to invent an entire response shape to say the same thing. `surface` is
 *  the procedure name, so what `meta.degraded` reports is the thing a person would go and look at. */
function optional<T>(
  degraded: { surface: string; code: string }[],
  surface: string,
  read: Promise<T>,
): Promise<T | null> {
  return read.catch((error: unknown) => {
    const code = absenceCode(error);
    if (!code) throw error;
    degraded.push({ surface, code });
    return null;
  });
}

/** The aggregate read model, assembled from the family procedures — so the aggregate is provably a composition of the
 *  same calls the screens make one by one. `DemoDataSchema.parse` rejects anything that drifted.
 *
 *  `mode` is the aggregate's provenance, and it is the *weakest* of its parts: Phase 2 makes six families live, but the
 *  aggregate still draws on simulated ones, so the honest answer for the whole is `simulated` until every contributing
 *  family is live. Reporting `live` while any part is a mock is precisely the overclaim ground rule 2 forbids. */
export async function composeDataset(
  c: AppClient,
  seed: string,
  mode: 'simulated' | 'live' = 'simulated',
): Promise<DemoData> {
  // Absences collected as the reads resolve, and carried on the aggregate so a screen can say which of its cards is
  // empty rather than leaving a trader to guess whether nothing happened or nothing loaded.
  const degraded: { surface: string; code: string }[] = [];
  const opt = <T>(surface: string, read: Promise<T>) => optional(degraded, surface, read);

  const [
    overview,
    accounts,
    ledger,
    rules,
    riskProfile,
    signals,
    brief,
    hub,
    notifs,
    prefs,
    conv,
    score,
    movers,
    investors,
    replay,
    eodList,
    eod,
    journal,
    metrics,
    splits,
    compare,
    insights,
    digest,
    progress,
    style,
    copy,
    backtest,
    tradesAll,
  ] = await Promise.all([
    c.dashboard.overview.get(),
    c.manage.accounts.list(),
    drain((cursor) => c.manage.accounts.ledger({ cursor, limit: 200 })),
    c.prepare.riskRules.catalogue(),
    c.prepare.riskProfile.get(),
    opt('execute.signals.list', c.execute.signals.list()),
    opt('prepare.newsBrief.get', c.prepare.newsBrief.get()),
    opt('execute.tradehub.snapshot', c.execute.tradehub.snapshot()),
    drain((cursor) => c.manage.notifications.feed({ cursor, limit: 200 })),
    c.me.preferences.get(),
    opt('voice.composer.history', c.voice.composer.history()),
    c.analyse.score.get(),
    opt('analyse.score.movers', c.analyse.score.movers()),
    opt('manage.investors.list', c.manage.investors.list()),
    opt('review.replay.get', c.review.replay.get()),
    opt(
      'review.eod.list',
      drainPages((cursor) => c.review.eod.list({ cursor, limit: 200 })),
    ),
    opt('review.eod.get', c.review.eod.get()),
    opt('review.journal.list', c.review.journal.list({ limit: 1 })),
    opt('analyse.analytics.metrics', c.analyse.analytics.metrics()),
    opt('analyse.analytics.splits', c.analyse.analytics.splits()),
    opt('analyse.compare.compare', c.analyse.compare.compare()),
    c.analyse.insights.list(),
    c.analyse.digest.list(),
    opt('review.progress.challengeStatus', c.review.progress.challengeStatus()),
    c.manage.styleProfile.get(),
    opt('execute.copy.providers', c.execute.copy.providers()),
    opt('review.backtest.results', c.review.backtest.results()),
    drainPages((cursor) => c.execute.trades.list({ cursor, limit: 200 })),
  ]);
  const data: DemoData = {
    meta: { seed, mode, asOf: DEMO_AS_OF, degraded },
    core: {
      exTrades: tradesAll.items,
      revDays: journal?.reviewedDays ?? [],
      revAccts: accounts.reviewAccounts,
      accounts: accounts.accounts,
      acctLedger: ledger,
      rulesMeta: rules.rules,
      rcMeta: riskProfile.controls,
      dashTrades: overview.recentTrades,
      signals: signals?.cards ?? [],
      news: brief?.news ?? [],
      calendar: brief?.calendar ?? [],
      posPending: hub?.rows.pending ?? [],
      posClosed: hub?.rows.closed ?? [],
      posOrders: hub?.rows.orders ?? [],
      notifList: notifs.map(({ id: _id, classZero: _c, ...n }) => n),
      setNav: prefs.sections,
      setPrefs: prefs.defs,
      aiConvGroups: conv?.groups ?? [],
      scorePillars: score.pillars,
      scoreMoved: movers?.items ?? [],
      replayMeta: replay?.metaRows ?? [],
      replaySess: replay?.sessionRows ?? [],
      replayAi: replay?.aiRows ?? [],
      eodKpis: eod?.kpis ?? [],
      eodHourly: eod?.hourly ?? [],
      eodRules: eod?.rules ?? [],
      scoreInvestors: investors?.interests ?? [],
      jEntries: journal?.tradeEntries ?? [],
      anStats: metrics?.stats ?? [],
      anSym: splits?.bySymbol ?? [],
      anStrat: splits?.byStrategy ?? [],
      anSess: splits?.bySession ?? [],
      sessions: overview.marketSessions,
      sessionsOld: overview.marketSessionsOld,
      ticker: overview.ticker,
      statCards: overview.statCards,
      dailyBars: overview.dailyBars,
      subtitle: overview.subtitle,
    },
    exec: {
      signals: signals?.rows ?? [],
      providers: copy?.items ?? [],
      quotes: hub?.quotes ?? [],
      tradeStrategies: tradesAll.first.strategies,
      tradeAccounts: tradesAll.first.accounts,
      copyStrategies: copy?.strategies ?? [],
    },
    review: {
      btBooks: backtest?.books ?? {},
      // A replay has a session and a grading or it does not exist; there is no half of one to render.
      replaySession: replay?.session ?? null,
      replayGrading: replay?.grading ?? null,
    },
    journal: { jrnDays: journal?.days ?? {} },
    ai: insights.data,
    score: score.data,
    digest: digest.data,
    compare: compare?.roster ?? { accounts: [] },
    analytics: metrics?.data ?? {
      logAccounts: [],
      journalDayKeys: [],
      symbolClasses: {},
      armedStrategies: [],
      baseCapital: 0,
    },

    progress: { apAccounts: progress?.accounts ?? [] },
    eod: {
      eodSessions: eodList?.items ?? [],
      eodBypassHistory: eodList?.first.bypassHistory ?? [],
    },
    style: style.data,
    dashboard: overview.dashboard,
  };
  return DemoDataSchema.parse(data);
}
